import type React from "react";
import { useCallback, useMemo, useState } from "react";

import { orderBy } from "lodash";

import { SearchInput } from "@similarweb/ui-components";

import type { FlexTableColumnConfig } from "app/components/React/Table/FlexTable/types";
import MultiColorSelectionTable from "app/components/React/Table/MultiColorSelectionTable";
import { defaultGetStableKey } from "app/components/React/Table/SWReactTable";
import type { ChannelKeyName, ChannelMMX6Canonical, MetricKeyNames, SegmentId } from "app/constants/MmxChannels";
import { i18nFilter } from "app/filters/i18nFilter";
import type { ISegmentsMMXGraphData } from "app/pages/segments/mmx/mmxGraphData";
import { SegmentsTableStyles } from "app/pages/segments/styleComponents";
import { type ICustomSegmentGroupWebsite, SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";

import { ChannelsObj } from "./ChannelsObj";
import { segmentsGroupMMXTableColumnsConfig } from "./SegmentsGroupMMXTableConfig";
import { SearchContainer, TableWrapper } from "./styledComponents";
import type { SegmentRowData } from "./SegmentsGroupMMXTableTypes";

export type { ColumnName, SegmentRowData } from "./SegmentsGroupMMXTableTypes";

interface ISegmentsGroupMMXTableProps {
    selectedRows?: {
        id: string;
        index: number;
        selectionColor: string;
    }[];
    selectedDisplayTypeIndex?: number;
    data?: ISegmentsMMXGraphData;
    isLoading?: boolean;
    tableSelectionKey: string;
    allSegments?: any;
    groups: {
        id: string;
        websites: ICustomSegmentGroupWebsite[];
        members: string[];
    }[];
    params?: any;
    selectedMetric: MetricKeyNames;
}

const sortedColumn: { field: string; sortDirection: "desc" | "asc" } = {
    field: "GroupVisitsShare",
    sortDirection: "desc",
};

const SegmentsGroupMMXTable: React.FC<ISegmentsGroupMMXTableProps> = (props) => {
    const { isLoading, data, selectedDisplayTypeIndex, allSegments, groups, params, selectedMetric } = props;
    const [searchString, setSearchString] = useState("");
    const isPercentage = useMemo(() => selectedDisplayTypeIndex === 1, [selectedDisplayTypeIndex]);
    const onSearch = useCallback((val: string) => {
        setSearchString(val.trim());
    }, []);

    const getStableKey = useCallback((index: number, col: FlexTableColumnConfig, row: any) => {
        if (row) {
            const { id } = row;
            return `${id}`;
        }
        return defaultGetStableKey(index, col);
    }, []);

    const manipulatedData = useMemo((): SegmentRowData[] | undefined => {
        if (!isLoading && data) {
            const tableData = data;
            let groupVisitsTotal = 0;
            return Object.keys(tableData ?? {})
                ?.map((segId: SegmentId) => {
                    const segmentTotalVisit = Object.values(tableData[segId].Monthly.Visits).reduce(
                        (acc, { Total }) => acc + (Total?.Value ?? 0),
                        0,
                    );
                    groupVisitsTotal += segmentTotalVisit;
                    const [segmentObj, segmentType] = SegmentsUtils.getSegmentObjectByKey(segId, {
                        segments: allSegments,
                        websites: groups.find((group) => group.id === params.id)?.websites,
                    });
                    const displayKey = selectedDisplayTypeIndex === 1 ? "Percentage" : "Value";
                    const item = tableData[segId].Monthly[selectedMetric];
                    const channelValues = (
                        Object.entries(ChannelsObj) as [ChannelMMX6Canonical, ChannelKeyName][]
                    ).reduce(
                        (acc, [canonical, apiKey]) => {
                            acc[canonical] =
                                canonical === "InternalReferrals" && segmentType === SEGMENT_TYPES.WEBSITE
                                    ? NaN
                                    : (item[apiKey]?.Total?.[displayKey] ?? NaN);
                            return acc;
                        },
                        {} as Record<ChannelMMX6Canonical, number>,
                    );
                    return {
                        ...segmentObj,
                        ...channelValues,
                        /* eslint-disable @typescript-eslint/naming-convention -- domain field names from the segment data model */
                        SegmentType: segmentType,
                        SegmentShare: tableData[segId].Meta?.SegmentShare,
                        Visits: segmentTotalVisit,
                        /* eslint-enable @typescript-eslint/naming-convention */
                    };
                })
                .map((segObj) => ({
                    ...segObj,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    GroupVisitsShare: segObj.Visits / groupVisitsTotal,
                }));
        }
        return undefined;
    }, [isLoading, data, params.id, selectedDisplayTypeIndex, allSegments, groups, selectedMetric]);

    const filteredData = useMemo(() => {
        if (manipulatedData) {
            const filteredData =
                searchString !== ""
                    ? manipulatedData.filter((dataItem) => {
                          if (!dataItem.id) {
                              return false;
                          }
                          const segmentData = SegmentsUtils.getSegmentById({ segments: allSegments }, dataItem.id);
                          return (
                              dataItem.domain.indexOf(searchString) !== -1 ||
                              (segmentData &&
                                  dataItem.domain
                                      .concat(" " + segmentData.segmentName)
                                      .toLowerCase()
                                      .indexOf(searchString.toLowerCase()) !== -1)
                          );
                      })
                    : manipulatedData;
            const currentGroup = groups.find((group) => group.id === params.id);
            if (currentGroup && currentGroup.members.length > filteredData.length) {
                currentGroup.members.map((memberId) => {
                    if (filteredData.every((segment) => segment.id !== memberId)) {
                        const [segmentObj, segmentType] = SegmentsUtils.getSegmentObjectByKey(memberId, {
                            segments: allSegments,
                            websites: currentGroup?.websites,
                        });
                        filteredData.push({
                            ...segmentObj,
                            /* eslint-disable @typescript-eslint/naming-convention */
                            SegmentType: segmentType,
                            id: memberId,
                            isDisabled: true, // do not show data cells
                            HasGraphData: false, // to disable row selection
                            GroupVisitsShare: 0, // sort this row to the end of table
                            rowClass: "segmentRowDisabled",
                            /* eslint-enable @typescript-eslint/naming-convention */
                        });
                    }
                });
            }
            return orderBy(filteredData, sortedColumn?.field, sortedColumn?.sortDirection);
        }
        return [];
    }, [searchString, manipulatedData, allSegments, sortedColumn]);

    const [minCellTable, maxCellTable] = useMemo(() => {
        if (!manipulatedData?.length) {
            return [0, 0];
        }
        return manipulatedData.reduce(
            ([min, max], rowData) => {
                (Object.keys(ChannelsObj) as ChannelMMX6Canonical[]).forEach((k) => {
                    const val = rowData[k];
                    if (val != null && !Number.isNaN(val)) {
                        min = Math.min(min, val);
                        max = Math.max(max, val);
                    }
                });
                return [min, max];
            },
            [Infinity, -Infinity],
        );
    }, [manipulatedData]);

    const tableProps = useMemo(() => {
        return {
            isLoading,
            tableData: {
                Data: filteredData,
                maxCellValue: maxCellTable,
                minCellValue: minCellTable,
            },
            tableColumns: segmentsGroupMMXTableColumnsConfig(isPercentage, selectedMetric),
            getStableKey: getStableKey,
        };
    }, [getStableKey, isLoading, filteredData, maxCellTable, minCellTable, isPercentage]);

    return (
        <TableWrapper loading={isLoading}>
            <SegmentsTableStyles />
            <SearchContainer>
                <SearchInput
                    defaultValue={searchString}
                    debounce={100}
                    onChange={onSearch}
                    placeholder={i18nFilter()("segment.analysis.marketingChannels.group.table.search.place.holder")}
                />
            </SearchContainer>
            <MultiColorSelectionTable
                {...props}
                tableProps={tableProps}
                tableData={filteredData}
            />
        </TableWrapper>
    );
};

export default SegmentsGroupMMXTable;
